import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Star, Upload, X } from 'lucide-react';
import { projectsAPI, categoriesAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    images: '',
    tools: '',
    approach: '',
    featured: false
  });

  const loadProjects = useCallback(async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
      // Set first category as default if available
      if (response.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0].name }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [formData.category]);

  useEffect(() => {
    loadProjects();
    loadCategories();
  }, [loadProjects, loadCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Combine uploaded images and URL images
    const allImages = [
      ...uploadedImages,
      ...formData.images.split(',').map(img => img.trim()).filter(img => img)
    ];
    
    const projectData = {
      ...formData,
      images: allImages,
      tools: formData.tools.split(',').map(tool => tool.trim()).filter(tool => tool)
    };

    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, projectData);
        toast.success('Project updated successfully!');
      } else {
        await projectsAPI.create(projectData);
        toast.success('Project created successfully!');
      }
      loadProjects();
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to save project');
      console.error('Error saving project:', error);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeUploadedImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    
    // Separate base64 images from URLs
    const base64Images = project.images.filter(img => img.startsWith('data:'));
    const urlImages = project.images.filter(img => !img.startsWith('data:'));
    
    setUploadedImages(base64Images);
    setFormData({
      title: project.title,
      description: project.description,
      category: project.category,
      images: urlImages.join(', '),
      tools: project.tools.join(', '),
      approach: project.approach,
      featured: project.featured
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectsAPI.delete(id);
      toast.success('Project deleted successfully!');
      loadProjects();
    } catch (error) {
      toast.error('Failed to delete project');
      console.error('Error deleting project:', error);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
    setUploadedImages([]);
    setFormData({
      title: '',
      description: '',
      category: categories.length > 0 ? categories[0].name : '',
      images: '',
      tools: '',
      approach: '',
      featured: false
    });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div data-testid="admin-projects-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold font-['Syne']" data-testid="projects-title">Projects</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black hover:bg-primary/90" 
                data-testid="add-project-button"
                onClick={() => loadCategories()}
              >
                <Plus size={18} className="mr-2" />
                Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl !h-[90vh] !flex !flex-col !p-0 gap-0" data-testid="project-dialog">
              <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-white/10">
                <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto flex-1 px-6 py-4">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="project-form">
                <div>
                  <Input
                    placeholder="Title"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                    data-testid="project-title-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    required
                    rows={4}
                    data-testid="project-description-input"
                  />
                </div>
                <div>
                  <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                    <SelectTrigger data-testid="project-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))}
                      {categories.length === 0 && (
                        <SelectItem value="no-categories" disabled>
                          No categories available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Image Upload Section */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Project Images</label>
                  
                  {/* Image URL Input */}
                  <div>
                    <Input
                      placeholder="Image URLs (comma-separated)"
                      value={formData.images}
                      onChange={(e) => handleChange('images', e.target.value)}
                      data-testid="project-images-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste image URLs separated by commas
                    </p>
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center gap-2 my-2">
                    <div className="flex-1 border-t border-white/10"></div>
                    <span className="text-xs text-muted-foreground">OR</span>
                    <div className="flex-1 border-t border-white/10"></div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mb-1">
                          Click to upload images
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Supports JPG, PNG, GIF (max 5MB each)
                        </p>
                      </div>
                    </label>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>

                  {/* Uploaded Images Preview */}
                  {uploadedImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {uploadedImages.map((img, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={img} 
                            alt={`Upload ${index + 1}`} 
                            className="w-full h-24 object-cover rounded border border-white/10"
                          />
                          <button
                            type="button"
                            onClick={() => removeUploadedImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Input
                    placeholder="Tools (comma-separated)"
                    value={formData.tools}
                    onChange={(e) => handleChange('tools', e.target.value)}
                    data-testid="project-tools-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Design Approach"
                    value={formData.approach}
                    onChange={(e) => handleChange('approach', e.target.value)}
                    rows={3}
                    data-testid="project-approach-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.featured}
                    onCheckedChange={(checked) => handleChange('featured', checked)}
                    data-testid="project-featured-switch"
                  />
                  <label className="text-sm">Featured Project</label>
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-primary text-black hover:bg-primary/90" data-testid="project-save-button">
                    {editingProject ? 'Update' : 'Create'} Project
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="project-cancel-button">
                    Cancel
                  </Button>
                </div>
              </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-20" data-testid="projects-loading">
            <p className="text-muted-foreground">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20" data-testid="no-projects">
            <p className="text-muted-foreground">No projects yet. Create your first one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="projects-list">
            {projects.map((project) => (
              <div key={project.id} className="bg-card border border-white/10 rounded-lg overflow-hidden" data-testid={`project-item-${project.id}`}>
                <div className="aspect-video bg-secondary">
                  {project.images[0] && (
                    <img src={project.images[0]} alt={project.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold font-['Syne']" data-testid={`project-title-${project.id}`}>{project.title}</h3>
                        {project.featured && <Star size={16} className="text-yellow-500 fill-yellow-500" />}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{project.category}</p>
                      <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(project)}
                      data-testid={`edit-project-${project.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(project.id)}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminProjects;
